import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Unauthenticated on first load is expected; only log unexpected errors.
      if (err?.response?.status && err.response.status !== 401) {
        logger.error("Auth refresh failed:", err);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout should always succeed client-side even if the server call fails
      // (e.g. offline). We log for observability but do not surface a toast.
      logger.warn("Server logout failed; clearing local session anyway:", err?.message || err);
    }
    setUser(false);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}
